import { EVENTS, publish } from 'ia-util-pubsub'

class IaVariantPicker extends HTMLElement {
  connectedCallback() {
    this.productInfo = new Map()

    if (this.closest('ia-quick-buy')) {
      this.updateUrl = false
    }
    this.addEventListener('change', this.handleVariantChange.bind(this))
    this.addEventListener('touchstart', this.handleElementEvent.bind(this))
    this.addEventListener('mousedown', this.handleElementEvent.bind(this))
  }

  handleElementEvent(event) {
    // TODO: evaluate creating a custom dropdown component to listen to touchstart and mousedown events
    const target = event.target.previousElementSibling

    if (target?.tagName !== 'INPUT') {
      return
    }

    this.updateOptions(target)
    this.updateMasterId()
    this.currentVariant && this.getProductInfo()
  }

  async handleVariantChange() {
    this.updateOptions()
    this.updateMasterId()
    this.updateVariantStatuses()

    if (this.currentVariant) {
      this.updateURLHandler()
      
      publish(EVENTS.variantLoading, {
        detail: {
          loading: true,
          sectionId: this.sectionId,
          productId: this.productId
        }
      })
      
      const html = await this.getProductInfo()

      publish(EVENTS.variantChange, {
        detail: {
          html,
          sectionId: this.sectionId,
          productId: this.productId,
          variant: this.currentVariant
        }
      })
      
      publish(EVENTS.variantLoading, {
        detail: {
          loading: false,
          sectionId: this.sectionId,
          productId: this.productId
        }
      })
    } else {
      publish(EVENTS.variantChange, {
        detail: {
          html: null,
          sectionId: this.sectionId,
          productId: this.productId,
          variant: null
        }
      })
    }
  }

  updateOptions(target) {
    this.options = Array.from(this.querySelectorAll('select, .js-option-radio'), (element) => {
      if (element.tagName === 'SELECT') {
        return element.value
      }

      if (element.classList.contains('js-option-radio')) {
        return Array.from(element.querySelectorAll('input')).find(
          (radio) => (target && radio === target) ?? radio.checked
        )?.value
      }
    })
  }

  updateMasterId() {
    this.currentVariant = this.getVariantData().find(
      (variant) => !variant.options.map((option, index) => this.options[index] === option).includes(false)
    )
  }

  getVariantData() {
    this.variantData = this.variantData || JSON.parse(this.querySelector('[type="application/json"]').textContent)
    return this.variantData
  }

  updateVariantStatuses() {
    const selectedOptionOneVariants = this.variantData.filter(
      (variant) => this.querySelector(':checked').value === variant.option1
    )
    const inputWrappers = [...this.querySelectorAll('fieldset')]
    inputWrappers.forEach((option, index) => {
      if (index === 0) return

      const optionInputs = [...option.querySelectorAll('input[type="radio"], option')]
      const previousOptionSelected = inputWrappers[index - 1].querySelector(':checked').value
      const availableOptionInputsValue = selectedOptionOneVariants
        .filter((variant) => variant.available && variant[`option${index}`] === previousOptionSelected)
        .map((variantOption) => variantOption[`option${index + 1}`])

      this.setInputAvailability(optionInputs, availableOptionInputsValue)
    })
  }

  setInputAvailability(elementList, availableValuesList) {
    elementList.forEach((element) => {
      const value = element.getAttribute('value')
      const availableElement = availableValuesList.includes(value)

      if (element.tagName === 'INPUT') {
        element.toggleAttribute('data-disabled', !availableElement)
      }
    })
  }

  updateURLHandler() {
    if (!this.currentVariant || !this.updateUrl) {
      return
    }

    window.history.replaceState({}, '', `${this.url}?variant=${this.currentVariant.id}`)
  }

  buildRequestUrl() {
    let productUrl = this.url
    const params = []

    // Add variant ID if available
    if (this.currentVariant && this.currentVariant.id) {
      params.push(`variant=${this.currentVariant.id}`)
    }

    // Determine section ID based on context
    const isInProductCard = this.closest('ia-product-card') !== null
    const sectionId = isInProductCard 
      ? 'section-rendering-product-card'
      : this.sectionId

    // Build URL with section_id parameter
    if (productUrl?.includes('?')) {
      productUrl = productUrl.split('?')[0]
    }
    
    return `${productUrl}?section_id=${sectionId}${params.length > 0 ? '&' + params.join('&') : ''}`
  }

  getProductInfo() {
    const requestUrl = this.buildRequestUrl()

    if (this.productInfo.has(requestUrl)) {
      return this.productInfo.get(requestUrl)
    }

    this.productInfo.set(
      requestUrl,
      fetch(requestUrl)
        .then((response) => response.text())
        .then((responseText) => new DOMParser().parseFromString(responseText, 'text/html'))
    )

    return this.productInfo.get(requestUrl)
  }

  get sectionId() {
    return this.getAttribute('section-id')
  }

  get productId() {
    return this.getAttribute('product-id')
  }

  get updateUrl() {
    return this.getAttribute('update-url') === 'true'
  }

  set updateUrl(value) {
    this.setAttribute('update-url', value)
  }

  get url() {
    return this.getAttribute('url')
  }
}

customElements.define('ia-product-options', IaVariantPicker)